import React, { useEffect, useRef, useState } from "react";
import { Send, MessagesSquare, Video } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useUser } from "../../context/UserContext";
import { getMyConversation, sendMyMessage } from "../../lib/api";
import VideoMeetingModal from "../common/VideoMeetingModal";

const formatTime = (ts) =>
  new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export default function PortalMessages() {
  const { user, token } = useUser();
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [callOpen, setCallOpen] = useState(false);
  const endRef = useRef(null);

  const callUrl = user ? `https://meet.jit.si/RFL-call-${user.id}` : "";

  useEffect(() => {
    if (!token) return;
    const load = () => getMyConversation(token).then(setConv).catch(() => {});
    load();
    const id = setInterval(load, 2500); // poll for back-office replies
    return () => clearInterval(id);
  }, [token]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conv?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const updated = await sendMyMessage(token, text.trim());
    setText("");
    setConv(updated);
  };

  const startCall = async () => {
    setCallOpen(true);
    try {
      const updated = await sendMyMessage(token, `Started a video call: ${callUrl}`);
      setConv(updated);
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col h-[calc(100vh-180px)] min-h-[480px]">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-navy text-gold flex items-center justify-center">
          <MessagesSquare className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="font-serif font-semibold text-navy">Your RFL Coaching Team</div>
          <div className="text-xs text-green-600 flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-green-500" /> We typically reply within a day
          </div>
        </div>
        <Button onClick={startCall} className="bg-gold hover:bg-gold-dark text-navy font-semibold h-9">
          <Video className="h-4 w-4 mr-1.5" /> Video Call
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-4 bg-cream/40">
        {conv?.messages?.map((m) => {
          const mine = m.sender === "client";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                <div
                  className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    mine
                      ? "bg-navy text-white rounded-br-md"
                      : "bg-white border border-gray-100 text-gray-700 rounded-bl-md shadow-sm"
                  }`}
                >
                  {m.text}
                </div>
                <span className="text-[11px] text-gray-400 mt-1 px-1">
                  {mine ? "You" : "RFL Team"} · {formatTime(m.ts)}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <form onSubmit={send} className="p-3 sm:p-4 border-t border-gray-100 flex items-center gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type your message..."
          className="flex-1"
        />
        <Button type="submit" className="bg-gold hover:bg-gold-dark text-navy font-semibold h-10 px-4">
          <Send className="h-4 w-4" />
        </Button>
      </form>

      <VideoMeetingModal
        open={callOpen}
        onOpenChange={setCallOpen}
        url={callUrl}
        title="Video Call with RFL"
        name={user?.name}
      />
    </div>
  );
}
